import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { LEGAL_DOCUMENTS } from "./legalDocuments";
import "../../styles/legal-pages.css";
import {
  getStoredThemePreference,
  resolveThemePreference,
  SYSTEM_THEME,
} from "../../utils/themePreference";

const META = {
  privacy: {
    tr: { eyebrow: "Gizlilik ve Veri Koruma", title: "Gizlilik Politikası", description: "Kişisel verilerin nasıl işlendiğini, saklandığını ve korunduğunu açıklayan resmi politika." },
    en: { eyebrow: "Privacy & Data Protection", title: "Privacy Policy", description: "The official policy explaining how personal data is processed, retained, and protected." },
  },
  terms: {
    tr: { eyebrow: "Hizmet Koşulları", title: "Kullanım Şartları", description: "Aile Yönetimi hizmetlerini kullanırken geçerli olan hak, yükümlülük ve kurallar." },
    en: { eyebrow: "Service Conditions", title: "Terms of Service", description: "The rights, responsibilities, and rules that apply when using Aile Yönetimi services." },
  },
  "account-deletion": {
    tr: { eyebrow: "Hesap ve Veri Kontrolü", title: "Hesap Silme", description: "Hesabınızı silme, verilerinizi dışa aktarma ve saklama süreleri hakkında bilgi." },
    en: { eyebrow: "Account & Data Control", title: "Account Deletion", description: "Information about deleting your account, exporting your data, and retention periods." },
  },
};

const FEEDBACK_PRIVACY_ADDENDUM = {
  tr: `
    <h2>Uygulama İçi Geri Bildirimler</h2>
    <p>Kullanıcı bir geri bildirim göndermeyi seçtiğinde mesaj içeriği, seçilen kategori ve isteğe bağlı olarak eklenen en fazla iki ekran görüntüsü işlenir. Kullanıcı bu bilgileri yalnızca Gönder düğmesine dokunduğunda iletir.</p>
    <p>Talebin incelenmesi, teknik sorunların teşhis edilmesi ve kullanıcıyla ilişkilendirilebilmesi amacıyla kullanıcı ve aile kimliği, ad soyad, kullanıcı adı, e-posta, uygulama sürümü, platform, işletim sistemi, cihaz modeli, dil, gönderim tarihi ve saati, IP adresi ve tarayıcı/uygulama kullanıcı aracısı geri bildirime otomatik olarak eklenir.</p>
    <p>Geri bildirimler ve ekleri yalnızca yetkili yönetici tarafından destek, güvenlik ve ürün geliştirme amaçlarıyla görüntülenir; reklam veya üçüncü taraf pazarlama amacıyla kullanılmaz. Kayıtlar gerekli olduğu süre boyunca korunur ve kullanıcı privacy@aileyonetimi.com üzerinden silme talebinde bulunabilir.</p>
  `,
  en: `
    <h2>In-App Feedback</h2>
    <p>When a user chooses to send feedback, the message, selected category, and up to two optional screenshots are processed. This information is transmitted only after the user taps the Send button.</p>
    <p>To review the request, diagnose technical issues, and associate it with the correct account, the user and family identifiers, full name, username, email, app version, platform, operating system, device model, language, submission date and time, IP address, and browser/app user agent are automatically attached to the feedback.</p>
    <p>Feedback and attachments are available only to authorized administrators for support, security, and product improvement. They are not used for advertising or third-party marketing. Records are retained only as long as necessary, and users may request deletion through privacy@aileyonetimi.com.</p>
  `,
};

const CURRENT_LEGAL_ADDENDA = {
  privacy: {
    tr: `
      <h2>21. Profil Fotoğrafları</h2>
      <p>Profil fotoğrafı eklemek isteğe bağlıdır. Kullanıcı yalnızca “Fotoğraf çek” veya “Galeriden seç” düğmesine dokunduğunda kamera ya da fotoğraf seçici açılır; avatar ekranının görüntülenmesi tek başına bir izin istemi başlatmaz.</p>
      <p>Seçilen görüntü cihaz üzerinde kullanıcı tarafından sürüklenebilir, yakınlaştırılabilir, döndürülebilir ve kare olarak kırpılır. Sunucu, güvenlik ve tutarlılık amacıyla sonucu 512×512 WebP biçiminde yeniden kodlar ve görüntüyle birlikte gelebilecek EXIF/konum meta verilerini saklamaz. Arka plan silme, yüz tanıma, biyometrik şablon oluşturma veya kimlik doğrulama yapılmaz.</p>
      <p>Profil fotoğrafı; kullanıcının kendi profilinde, üye olduğu aile alanlarında ve avatarın kullanıldığı sohbet, bildirim ve benzeri uygulama görünümlerinde gösterilir. Dosya <code>profilepicture</code> alanında kullanıcı başına tek aktif kopya olarak tutulur. Yeni fotoğraf eskisinin yerini alır; kullanıcı fotoğrafı kaldırdığında veya hesabını sildiğinde aktif profil fotoğrafı fiziksel depolamadan silinir.</p>
      <p>Yetkili aile üyeleri, manuel olarak oluşturdukları çocuk, bebek veya evcil hayvan takip profiline isteğe bağlı bir fotoğraf ekleyebilir. Bu fotoğraf ilgili aile alanındaki profil ve takip kartlarında aile üyelerine gösterilir. Her takip profili için yalnızca bir aktif fotoğraf tutulur; yeni yükleme öncekinin yerini alır ve fotoğraf kaldırıldığında veya ilgili takip profili silindiğinde aktif dosya fiziksel depolamadan silinir. Çocuğa veya başka bir kişiye ait görüntüyü ekleyen kullanıcı, gerekli ebeveynlik, velayet, temsil ya da diğer hukuki yetkiye ve izne sahip olmalıdır.</p>

      <h2>22. Apple Sağlık Verileri</h2>
      <p>iOS kullanıcısı açıkça izin verirse uygulama Apple Sağlık’tan seçilen adım, aktif enerji, yürüme/koşma mesafesi, egzersiz, uyku ve kalp atış hızı verilerini okuyarak kişisel günlük sağlık özetinde gösterebilir. Erişim salt okunurdur, kullanıcı Apple Sağlık izinlerini dilediği zaman iOS Ayarları veya Sağlık uygulamasından değiştirebilir.</p>
      <p>Apple Sağlık’tan okunan bu veriler uygulamanın sunucusuna yüklenmez; bu cihazdaki özet görünümünde işlenir. Reklam, pazarlama, kullanıcı profilleme veya analitik amacıyla kullanılmaz ve üçüncü taraflara satılmaz ya da paylaşılmaz. Sağlık özeti tıbbi teşhis veya profesyonel sağlık tavsiyesi değildir.</p>

      <h2>23. Hesap Yaşam Döngüsü Yönetici Bildirimleri</h2>
      <p>Yeni hesap kaydı veya kullanıcının kendi hesabını silmesi gibi sınırlı hesap yaşam döngüsü olaylarında, hizmetin işletilmesi ve güvenliği için yetkili süper yöneticilere uygulama içi ve/veya push bildirimi gönderilebilir. Bildirim; olay türü ve zamanı, kayıt yöntemi ve mevcutsa kullanıcının görünen adı gibi olayın anlaşılması için gerekli sınırlı bilgileri içerebilir. Bu bildirimler reklam amacıyla kullanılmaz ve yalnızca yetkili süper yönetici hesaplarına yöneltilir.</p>
    `,
    en: `
      <h2>21. Profile Photos</h2>
      <p>Adding a profile photo is optional. The camera or photo picker opens only after the user taps “Take photo” or “Choose from library”; merely viewing the avatar screen does not trigger a permission request.</p>
      <p>The selected image can be moved, zoomed, rotated, and cropped into a square by the user on the device. For security and consistency, the server re-encodes the result as a 512×512 WebP image and does not retain EXIF or location metadata that may have accompanied the source image. The service does not remove the background, perform face recognition, create biometric templates, or use the photo for identity verification.</p>
      <p>The profile photo appears on the user’s profile, in family workspaces they join, and in app surfaces that display avatars, such as chat and notifications. One active copy per user is kept in the <code>profilepicture</code> storage area. A new photo replaces the old one; the active profile photo is physically deleted when the user removes it or deletes the account.</p>
      <p>Authorized family members may optionally add a photo to a manually created child, baby, or pet tracking profile. The photo is shown to family members in that family workspace on the related profile and tracking cards. Only one active photo is retained per tracking profile; a new upload replaces the previous file, and the active file is physically deleted when the photo or the related tracking profile is removed. A user who uploads an image of a child or another person must have the necessary parental, custodial, representative, or other legal authority and permission.</p>

      <h2>22. Apple Health Data</h2>
      <p>If an iOS user expressly grants permission, the app may read selected steps, active energy, walking/running distance, exercise, sleep, and heart-rate data from Apple Health to show a private daily health summary. Access is read-only, and the user can change Apple Health permissions at any time in iOS Settings or the Health app.</p>
      <p>Apple Health data read for this feature is not uploaded to the app’s server; it is processed in the summary view on that device. It is not used for advertising, marketing, profiling, or analytics, and is not sold or shared with third parties. The health summary is not a medical diagnosis or professional medical advice.</p>

      <h2>23. Account Lifecycle Administrator Notices</h2>
      <p>For limited account lifecycle events, such as a new registration or a user deleting their own account, authorized super administrators may receive an in-app and/or push notice for service operation and security. A notice may include the event type and time, registration method, and—where available—the user’s display name, limited to what is needed to understand the event. These notices are not used for advertising and are directed only to authorized super administrator accounts.</p>
    `,
  },
  terms: {
    tr: `
      <h2>21. Profil Fotoğrafı Kuralları</h2>
      <p>Kullanıcı, profil fotoğrafı olarak yüklediği görüntüyü kullanma hakkına sahip olduğunu ve görüntünün hukuka, kişilik haklarına, telif haklarına ve topluluk güvenliğine aykırı olmadığını kabul eder. Başka bir kişiye ait fotoğraf ancak gerekli izin alınmışsa kullanılmalıdır. Müstehcen, yanıltıcı, tehdit edici, nefret içerikli veya hukuka aykırı görüntüler yasaktır.</p>
      <p>Fotoğraf avatar alanına uyum sağlaması için kullanıcı tarafından kırpılır ve hizmet tarafından sıkıştırılıp yeniden boyutlandırılır. Fotoğraf aile alanlarında ve avatarın gösterildiği uygulama bölümlerinde görülebilir. Kullanıcı fotoğrafı dilediği zaman kaldırabilir veya hazır avatar/baş harf görünümüne dönebilir.</p>
      <p>Çocuk, bebek veya evcil hayvan takip profiline fotoğraf ekleyen kullanıcı, fotoğrafı yüklemeye ve ilgili aile üyeleriyle paylaşmaya yetkili olduğunu kabul eder. Hazır avatar seçilmesi yüklenen dosyayı otomatik olarak silmez; dosya, kullanıcı açıkça kaldırana veya ilgili takip profili silinene kadar tek kopya olarak saklanabilir.</p>
      <h2>22. Sağlık Özelliklerinin Niteliği</h2>
      <p>Apple Sağlık özeti ve uygulamadaki diğer sağlık alanları yalnızca bilgilendirme ve kişisel düzenleme amaçlıdır; teşhis, tedavi, acil durum hizmeti veya hekim tavsiyesi yerine geçmez. Acil veya tıbbi bir durumda yetkili sağlık kuruluşuna başvurulmalıdır.</p>
    `,
    en: `
      <h2>21. Profile Photo Rules</h2>
      <p>The user confirms that they have the right to use any image uploaded as a profile photo and that it does not violate law, personality rights, copyright, or community safety. A photo of another person may be used only with the necessary permission. Obscene, deceptive, threatening, hateful, or unlawful images are prohibited.</p>
      <p>The user crops the image to fit the avatar area, and the service compresses and resizes it. The photo may be visible in family workspaces and app areas that display avatars. The user may remove it at any time or return to a built-in avatar or initials.</p>
      <p>A user who adds a photo to a child, baby, or pet tracking profile confirms that they are authorized to upload it and share it with the relevant family members. Selecting a built-in avatar does not automatically delete the uploaded file; one copy may remain available until the user explicitly removes it or deletes the related tracking profile.</p>
      <h2>22. Nature of Health Features</h2>
      <p>The Apple Health summary and other health areas in the app are provided only for information and personal organization. They are not a diagnosis, treatment, emergency service, or substitute for professional medical advice. Contact a qualified healthcare provider for emergencies or medical concerns.</p>
    `,
  },
  "account-deletion": {
    tr: `
      <h2>7. Profil Fotoğrafı ve Cihazdaki Sağlık Verileri</h2>
      <p>Hesap silme tamamlandığında sunucudaki aktif profil fotoğrafı ve profil fotoğrafı referansı silinir. Daha önce kullanılan fotoğraf yeni yüklemede zaten değiştirilmiş olduğundan kullanıcı başına kalıcı olarak birden fazla profil fotoğrafı tutulmaz.</p>
      <p>Apple Sağlık’tan okunan özet verileri sunucu hesabında saklanmadığı için sunucuda silinecek ayrı bir Apple Sağlık veri kopyası bulunmaz. Kullanıcı iOS üzerindeki Sağlık erişimini ayrıca Ayarlar veya Sağlık uygulamasından kapatabilir.</p>
      <p>Hesap silme olayı, hizmet güvenliği ve operasyonel denetim amacıyla yetkili süper yöneticilere sınırlı bir sistem bildirimi olarak iletilebilir. Bu olay kaydı, silinen hesabın yeniden kullanılmasını sağlamaz ve hesabın diğer profil, aile, oturum ve içerik verilerinin silinmesini engellemez.</p>
      <p>Çocuk, bebek veya evcil hayvan takip profilleri aile içinde ortak veri olabilir. Hesabın silinmesi bu ortak profilleri ve fotoğraflarını otomatik olarak kaldırmayabilir; ilgili fotoğraf, ailede yetkili bir kullanıcı tarafından kaldırıldığında veya takip profili silindiğinde aktif depolamadan silinir.</p>
    `,
    en: `
      <h2>7. Profile Photo and On-Device Health Data</h2>
      <p>When account deletion completes, the active profile photo and its profile reference are deleted from server storage. Because each new upload replaces the previous photo, the service does not persist multiple profile photos per user.</p>
      <p>Apple Health summary data is not stored in the server account, so there is no separate server-side Apple Health copy to delete. The user may also revoke Health access in iOS Settings or the Health app.</p>
      <p>The account deletion event may be delivered to authorized super administrators as a limited system notice for service security and operational review. This event record cannot reactivate the deleted account and does not prevent deletion of the account’s other profile, family, session, and content data.</p>
      <p>Child, baby, or pet tracking profiles may be shared family data. Deleting one account may not automatically remove those shared profiles or their photos; the active file is deleted when an authorized family user removes the photo or deletes the related tracking profile.</p>
    `,
  },
};

function currentLegalHtml(baseHtml) {
  return String(baseHtml || "")
    .replace(/Sürüm:\s*\d+(?:\.\d+)*(?:\s*\(\d+\))?/g, "Sürüm: 1.7.1 (22)")
    .replace(/Version:\s*\d+(?:\.\d+)*(?:\s*\(\d+\))?/g, "Version: 1.7.1 (22)")
    .replace(/Son Güncelleme:\s*[^<]+/g, "Son Güncelleme: 1 Ekim 2026")
    .replace(/Last Updated:\s*[^<]+/g, "Last Updated: October 1, 2026");
}

function resolveTheme() {
  return resolveThemePreference(getStoredThemePreference());
}

export default function LegalDocumentPage({ documentKey, forcedLang, embedded = false, onBack, onNavigate, transitionDirection = "left" }) {
  const { lang, setLang } = useLanguage();
  const activeLang = forcedLang || (lang === "en" ? "en" : "tr");
  const meta = META[documentKey]?.[activeLang] || META.privacy[activeLang];
  const baseHtml = currentLegalHtml(LEGAL_DOCUMENTS[documentKey]?.[activeLang] || "");
  const html = `${baseHtml}${documentKey === "privacy" ? FEEDBACK_PRIVACY_ADDENDUM[activeLang] : ""}${CURRENT_LEGAL_ADDENDA[documentKey]?.[activeLang] || ""}`;
  const touchStartRef = useRef(null);
  const mainRef = useRef(null);

  useEffect(() => {
    if (forcedLang && forcedLang !== lang) setLang(forcedLang);
    const themePreference = getStoredThemePreference();
    const applyTheme = () => {
      const theme = resolveTheme();
      document.body.setAttribute("data-tema", theme);
      document.documentElement.setAttribute("data-tema", theme);
      document.body.style.colorScheme = theme;
      document.documentElement.style.colorScheme = theme;
    };
    applyTheme();

    const media = themePreference === SYSTEM_THEME && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
    if (media) {
      if (typeof media.addEventListener === "function") media.addEventListener("change", applyTheme);
      else media.addListener(applyTheme);
    }
    document.documentElement.lang = activeLang;
    if (!embedded) document.title = `${meta.title} | Aile Yönetimi`;
    return () => {
      if (media) {
        if (typeof media.removeEventListener === "function") media.removeEventListener("change", applyTheme);
        else media.removeListener(applyTheme);
      }
      if (!embedded) document.title = "Aile Yönetimi";
    };
  }, [activeLang, embedded, forcedLang, lang, meta.title, setLang]);

  const navItems = useMemo(() => ([
    { key: "privacy", path: "/privacy", tr: "Gizlilik", en: "Privacy" },
    { key: "terms", path: "/terms", tr: "Şartlar", en: "Terms" },
    { key: "account-deletion", path: "/account-deletion", tr: "Hesap Silme", en: "Account Deletion" },
  ]), []);

  const returnToApp = useCallback((event) => {
    event.preventDefault();

    if (embedded && typeof onBack === "function") {
      onBack();
      return;
    }

    const sameOriginReferrer = (() => {
      try {
        return Boolean(document.referrer) && new URL(document.referrer).origin === window.location.origin;
      } catch {
        return false;
      }
    })();

    if (window.history.length > 1 && sameOriginReferrer) {
      window.history.back();
      return;
    }

    window.location.assign("/");
  }, [embedded, onBack]);

  const openDocument = useCallback((event, item) => {
    if (!embedded || typeof onNavigate !== "function") return;
    event.preventDefault();
    const currentIndex = navItems.findIndex((navItem) => navItem.key === documentKey);
    const nextIndex = navItems.findIndex((navItem) => navItem.key === item.key);
    onNavigate(item.key, nextIndex >= currentIndex ? "left" : "right");
  }, [documentKey, embedded, navItems, onNavigate]);

  const handleTouchStart = useCallback((event) => {
    if (!embedded || event.touches.length !== 1) return;
    const touch = event.touches[0];
    // Sol kenardaki ilk %12, uygulamanın global swipe-to-back hareketine ayrılır.
    if (touch.clientX < window.innerWidth * 0.12) {
      touchStartRef.current = null;
      return;
    }
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: event.timeStamp };
  }, [embedded]);

  const handleTouchEnd = useCallback((event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = Math.abs(touch.clientY - start.y);
    const elapsed = Math.max(1, event.timeStamp - start.time);
    const velocity = Math.abs(dx) / elapsed;
    if (dy > 90 || Math.abs(dx) < 55 || Math.abs(dx) < dy * 1.15 || (velocity < 0.18 && Math.abs(dx) < window.innerWidth * 0.22)) return;

    const currentIndex = navItems.findIndex((item) => item.key === documentKey);
    const nextIndex = dx < 0 ? currentIndex + 1 : currentIndex - 1;
    const nextItem = navItems[nextIndex];
    if (!nextItem || typeof onNavigate !== "function") return;

    onNavigate(nextItem.key, dx < 0 ? "left" : "right");
    requestAnimationFrame(() => mainRef.current?.scrollTo?.({ top: 0, behavior: "auto" }));
  }, [documentKey, navItems, onNavigate]);

  return (
    <div className={`legalPageShell${embedded ? " legalPageShellEmbedded" : ""}`} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <header className="legalTopbar">
        <a className="legalBrand" href="/" onClick={returnToApp} aria-label="Aile Yönetimi">
          <img src="/logo512.png" alt="" />
          <span><strong>Aile Yönetimi</strong><small>{activeLang === "en" ? "Family, organized." : "Aile, düzen içinde."}</small></span>
        </a>
        <div className="legalTopActions">
          <div className="legalLanguage" role="group" aria-label={activeLang === "en" ? "Language" : "Dil"}>
            <button className={activeLang === "tr" ? "active" : ""} onClick={() => setLang("tr")}>TR</button>
            <button className={activeLang === "en" ? "active" : ""} onClick={() => setLang("en")}>EN</button>
          </div>
          <a className="legalHomeButton" href="/" onClick={returnToApp} aria-label={activeLang === "en" ? "Back to app" : "Uygulamaya dön"}>
            <span className="legalHomeButtonIcon" aria-hidden="true">‹</span>
            <span className="legalHomeButtonText">{activeLang === "en" ? "Back to App" : "Uygulamaya Dön"}</span>
          </a>
        </div>
      </header>

      <main ref={mainRef} className="legalMain">
        <div key={documentKey} className={`legalTabTransition legalTabEnter-${transitionDirection}`}>
        <section className="legalHero">
          <div className="legalHeroGlow" />
          <span className="legalEyebrow">{meta.eyebrow}</span>
          <h1>{meta.title}</h1>
          <p>{meta.description}</p>
          <div className="legalTrustRow">
            <span>✓ {activeLang === "en" ? "No advertising" : "Reklam yok"}</span>
            <span>✓ {activeLang === "en" ? "Privacy-safe analytics" : "Gizlilik odaklı analitik"}</span>
            <span>✓ {activeLang === "en" ? "User-controlled sharing" : "Kullanıcı kontrollü paylaşım"}</span>
          </div>
        </section>

        <nav className="legalDocumentNav" aria-label={activeLang === "en" ? "Legal documents" : "Yasal belgeler"}>
          {navItems.map((item) => (
            <a key={item.key} href={item.path} onClick={(event) => openDocument(event, item)} className={documentKey === item.key ? "active" : ""}>
              {item[activeLang]}
            </a>
          ))}
        </nav>

        <article className="legalDocumentCard">
          <div className="legalDocumentContent" dangerouslySetInnerHTML={{ __html: html }} />
        </article>

        <section className="legalContactCard">
          <div><span>{activeLang === "en" ? "Privacy contact" : "Gizlilik iletişimi"}</span><a href="mailto:privacy@aileyonetimi.com">privacy@aileyonetimi.com</a></div>
          <div><span>{activeLang === "en" ? "General support" : "Genel destek"}</span><a href="mailto:help@aileyonetimi.com">help@aileyonetimi.com</a></div>
        </section>
        </div>
      </main>

      <footer className="legalFooter">YAVUZ SOFT © 2026 · Aile Yönetimi</footer>
    </div>
  );
}
